import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { getEventSource } from '../../utils/eventSource';
import radarTilesService from '../../api/radarTilesService';
import activityService, { type ActivityPeriod, type ActivityResponse, type ActivityAvailability, computeActivityRanges } from '../../api/activityService';
import forecastService, { type ForecastDaily, type ForecastHourly, type ForecastRain, type ForecastMinutely } from '../../api/forecastService';
import stationObservationsService, { type StationData } from '../../api/stationObservationsService';
import { isTioProvider, type MapTileProvider } from './useNowcastingState';
import { circuitsService, type CircuitWithGeoJSON } from '../../api/circuitsService';
import infosService, { type EventInfo } from '../../api/infosService';
import type { EventWithCircuit } from '../../api/eventsService';
import { type ForecastAvailability } from '../../utils/forecastTypes';

dayjs.extend(utc);
dayjs.extend(timezone);

// Export RadarTile type for use in NowcastingPage
export interface RadarTile {
  timestamp: string;
  url: string;
  datetime: dayjs.Dayjs;
  isNowcast?: boolean;
  forecastOffset?: number;
}

interface UseDataFetchingParams {
  selectedEvent: EventWithCircuit | null;
  observationsEnabled: boolean;
  isEventActive: boolean;
  autoLoop: boolean;
  isPlaying: boolean;
  mapTileProvider: MapTileProvider;
  /** When not looping in live mode: dock to 'first', 'current', 'now', or 'last' tile on refresh */
  playbackDock: 'first' | 'current' | 'now' | 'last';
  /** Ref with current tile's datetime (ms) - used when dock is 'current' to find closest match */
  currentTileDatetimeMsRef: React.RefObject<number | null>;
  setIsPlaying: (playing: boolean) => void;
  setIsTioPlaying: (playing: boolean) => void;
  setSelectedTimestamp: (timestamp: Date | null) => void;
  setCurrentIndex: (index: number | ((prev: number) => number)) => void;
  setTiles: React.Dispatch<React.SetStateAction<RadarTile[]>>;
  setRadarL2NowcastTiles: React.Dispatch<React.SetStateAction<RadarTile[]>>;
  hasManuallyPausedRef: React.RefObject<boolean>;
  currentIndexRef: React.RefObject<number>;
  refreshCounter?: number;
}

interface UseDataFetchingReturn {
  // State values
  radarActivity: ActivityPeriod[];
  stationActivity: ActivityPeriod[];
  circuitData: CircuitWithGeoJSON | null;
  dailyForecasts: ForecastDaily[];
  hourlyForecasts: ForecastHourly[];
  rainForecasts: ForecastRain[];
  minutelyForecasts: ForecastMinutely[];
  infos: EventInfo[];
  currentInfoIndex: number;
  stations: StationData[];
  selectedStationIndex: number;
  selectedDateRange: { start: Date; end: Date } | null;
  loading: boolean;
  error: string | null;
  tilesSource: string;

  // Setters needed by component
  setCurrentInfoIndex: React.Dispatch<React.SetStateAction<number>>;
  setSelectedStationIndex: React.Dispatch<React.SetStateAction<number>>;
  setStations: React.Dispatch<React.SetStateAction<StationData[]>>;
  setSelectedDateRange: React.Dispatch<React.SetStateAction<{ start: Date; end: Date } | null>>;
  setRadarActivity: React.Dispatch<React.SetStateAction<ActivityPeriod[]>>;
  setStationActivity: React.Dispatch<React.SetStateAction<ActivityPeriod[]>>;
  setDailyForecasts: React.Dispatch<React.SetStateAction<ForecastDaily[]>>;
  setHourlyForecasts: React.Dispatch<React.SetStateAction<ForecastHourly[]>>;
  setRainForecasts: React.Dispatch<React.SetStateAction<ForecastRain[]>>;
  setMinutelyForecasts: React.Dispatch<React.SetStateAction<ForecastMinutely[]>>;
  setInfos: React.Dispatch<React.SetStateAction<EventInfo[]>>;
  setCircuitData: React.Dispatch<React.SetStateAction<CircuitWithGeoJSON | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setTilesSource: React.Dispatch<React.SetStateAction<string>>;

  // Computed values
  radarActivityAvailability: ActivityAvailability | undefined;
  stationActivityAvailability: ActivityAvailability | undefined;
  forecastAvailability: ForecastAvailability;

  // Handlers
  handleDateRangeChange: (startDate: Date, endDate: Date) => void;
  refreshActivityData: () => Promise<void>;

  // Refs
  debounceTimerRef: React.RefObject<ReturnType<typeof setTimeout> | null>;
}

/**
 * Custom hook to manage data fetching for the Nowcasting page.
 * Handles:
 * - fetchAvailability: Fetch all event data when event changes
 * - handleDateRangeChange: Fetch tiles and observations when date range changes (debounced)
 * - refreshActivityData: Refresh activity data on tab visibility change
 */
export function useDataFetching({
  selectedEvent,
  observationsEnabled,
  isEventActive,
  autoLoop,
  isPlaying,
  mapTileProvider,
  playbackDock,
  currentTileDatetimeMsRef,
  setIsPlaying,
  setIsTioPlaying,
  setSelectedTimestamp,
  setCurrentIndex,
  setTiles,
  setRadarL2NowcastTiles,
  hasManuallyPausedRef,
  currentIndexRef,
  refreshCounter = 0,
}: UseDataFetchingParams): UseDataFetchingReturn {
  // State for activity data
  const [radarActivity, setRadarActivity] = useState<ActivityPeriod[]>([]);
  const [stationActivity, setStationActivity] = useState<ActivityPeriod[]>([]);

  // State for circuit and forecast data
  const [circuitData, setCircuitData] = useState<CircuitWithGeoJSON | null>(null);
  const [dailyForecasts, setDailyForecasts] = useState<ForecastDaily[]>([]);
  const [hourlyForecasts, setHourlyForecasts] = useState<ForecastHourly[]>([]);
  const [rainForecasts, setRainForecasts] = useState<ForecastRain[]>([]);
  const [minutelyForecasts, setMinutelyForecasts] = useState<ForecastMinutely[]>([]);
  const [infos, setInfos] = useState<EventInfo[]>([]);
  const [currentInfoIndex, setCurrentInfoIndex] = useState(0);

  // State for stations
  const [stations, setStations] = useState<StationData[]>([]);
  const [selectedStationIndex, setSelectedStationIndex] = useState(0);
  const [selectedDateRange, setSelectedDateRange] = useState<{ start: Date; end: Date } | null>(null);

  // Loading and error state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tilesSource, setTilesSource] = useState<string>('prod');

  // Refs
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Use refs for values that shouldn't trigger callback recreation
  const isPlayingRef = useRef(isPlaying);
  const autoLoopRef = useRef(autoLoop);
  const isEventActiveRef = useRef(isEventActive);
  const playbackDockRef = useRef(playbackDock);

  // Keep refs in sync with state
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    autoLoopRef.current = autoLoop;
  }, [autoLoop]);

  useEffect(() => {
    isEventActiveRef.current = isEventActive;
  }, [isEventActive]);

  useEffect(() => {
    playbackDockRef.current = playbackDock;
  }, [playbackDock]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Ref to track the activity refresh interval
  const activityRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Derive the radar activity source based on the active map tile provider.
  // Legacy events only have data in the old pipeline, so always use 'legacy' for them
  // regardless of the current mapTileProvider (avoids race with the auto-switch effect).
  // When on the L2 layer for non-legacy events, query l2_radar_tiles.
  const radarActivitySource = useMemo(() => {
    if (selectedEvent && getEventSource(selectedEvent) === 'legacy') return 'legacy' as const;
    return 'l2' as const;
  }, [selectedEvent]);

  // Function to refresh activity data (used on interval for live events)
  const refreshActivityData = useCallback(async () => {
    if (!selectedEvent || !isEventActive) return;

    try {
      const source = getEventSource(selectedEvent);

      const isLegacy = source === 'legacy';
      const minutelyForecastPromise = isLegacy
        ? Promise.resolve({ forecasts: [] })
        : forecastService.getForecastMinutely({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          });
      const [radarResponse, stationResponse, minutelyResponse] = observationsEnabled
        ? await Promise.allSettled([
          activityService.getRadarActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source: radarActivitySource,
          }),
          activityService.getStationActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
          minutelyForecastPromise,
        ] as const)
        : [
          { status: 'fulfilled' as const, value: { success: true, count: 0, activities: [] } },
          { status: 'fulfilled' as const, value: { success: true, count: 0, activities: [] } },
          await minutelyForecastPromise
            .then((value) => ({ status: 'fulfilled' as const, value }))
            .catch((reason) => ({ status: 'rejected' as const, reason })),
        ];

      if (radarResponse.status === 'fulfilled' && radarResponse.value.activities.length > 0) {
        setRadarActivity(radarResponse.value.activities);
        console.log('[Nowcasting] Refreshed radar activity');
      }

      if (stationResponse.status === 'fulfilled' && stationResponse.value.activities.length > 0) {
        setStationActivity(stationResponse.value.activities);
        console.log('[Nowcasting] Refreshed station activity');
      }

      if (minutelyResponse.status === 'fulfilled' && minutelyResponse.value.forecasts.length > 0) {
        setMinutelyForecasts(minutelyResponse.value.forecasts);
        console.log('[Nowcasting] Refreshed minutely forecasts');
      }

    } catch (err) {
      console.error('[Nowcasting] Error refreshing live data:', err);
    }
  }, [selectedEvent, isEventActive, observationsEnabled, radarActivitySource]);

  // Set up 5-minute interval to refresh activity data for live events
  // NOTE: We intentionally do NOT refresh on visibility/focus changes anymore.
  // This was causing bad UX where the player would reset to first tile when
  // switching browser tabs. Instead, we use a periodic interval for live events.
  useEffect(() => {
    // Clear any existing interval
    if (activityRefreshIntervalRef.current) {
      clearInterval(activityRefreshIntervalRef.current);
      activityRefreshIntervalRef.current = null;
    }

    // Only set up interval for active events
    if (!isEventActive) {
      return;
    }

    // Refresh live data every 5 minutes for active events
    const ACTIVITY_REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
    
    console.log('[Nowcasting] Setting up 5-minute live refresh interval for active event');
    activityRefreshIntervalRef.current = setInterval(() => {
      console.log('[Nowcasting] 5-minute interval: refreshing live data');
      refreshActivityData();
    }, ACTIVITY_REFRESH_INTERVAL);

    return () => {
      if (activityRefreshIntervalRef.current) {
        clearInterval(activityRefreshIntervalRef.current);
        activityRefreshIntervalRef.current = null;
      }
    };
  }, [isEventActive, refreshActivityData]);

  // Fetch all availability data when event changes
  useEffect(() => {
    if (!selectedEvent) {
      setRadarActivity([]);
      setStationActivity([]);
      setCircuitData(null);
      setDailyForecasts([]);
      setHourlyForecasts([]);
      setRainForecasts([]);
      setInfos([]);
      setCurrentInfoIndex(0);
      return;
    }

    const fetchAvailability = async () => {
      try {
        const source = getEventSource(selectedEvent);

        const activityPromises: Promise<Awaited<ReturnType<typeof activityService.getRadarActivity>>>[] = observationsEnabled ? [
          activityService.getRadarActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source: radarActivitySource,
          }),
          activityService.getStationActivity({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
        ] : [
          Promise.resolve({ success: true, count: 0, activities: [] }),
          Promise.resolve({ success: true, count: 0, activities: [] }),
        ];

        // Fetch all data in parallel
        const [radarResponse, stationResponse, circuit, dailyResponse, hourlyResponse, rainResponse, minutelyResponse, infosResponse] = await Promise.allSettled([
          ...activityPromises,
          circuitsService.getCircuitById(selectedEvent.circuit_id, selectedEvent.circuit_geojson_id),
          forecastService.getForecastDaily({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
          forecastService.getForecastHourly({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
          forecastService.getForecastRain({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
          source === 'legacy'
            ? Promise.resolve({ forecasts: [] })
            : forecastService.getForecastMinutely({
                season_year: selectedEvent.season_year,
                circuit_id: selectedEvent.circuit_id,
                start_date: selectedEvent.start_date,
                source,
              }),
          infosService.getEventInfos({
            season_year: selectedEvent.season_year,
            circuit_id: selectedEvent.circuit_id,
            start_date: selectedEvent.start_date,
            source,
          }),
        ]);

        // Set radar activity (only if observations enabled)
        if (observationsEnabled) {
          if (radarResponse.status === 'fulfilled') {
            const radarValue = radarResponse.value as ActivityResponse;
            if (radarValue.activities && radarValue.activities.length > 0) {
              setRadarActivity(radarValue.activities);
            } else {
              setRadarActivity([]);
            }
          } else {
            setRadarActivity([]);
          }

          // Set station activity
          if (stationResponse.status === 'fulfilled') {
            const stationValue = stationResponse.value as ActivityResponse;
            if (stationValue.activities && stationValue.activities.length > 0) {
              setStationActivity(stationValue.activities);
            } else {
              setStationActivity([]);
            }
          } else {
            setStationActivity([]);
          }
        } else {
          // Clear activity when observations are disabled
          setRadarActivity([]);
          setStationActivity([]);
        }

        // Set circuit data
        if (circuit.status === 'fulfilled') {
          const circuitValue = circuit.value as CircuitWithGeoJSON;
          if ('geojson' in circuitValue) {
            setCircuitData(circuitValue);
          } else {
            setCircuitData(null);
          }
        } else {
          setCircuitData(null);
        }

        // Set daily forecasts
        if (dailyResponse.status === 'fulfilled') {
          const dailyValue = dailyResponse.value as Awaited<ReturnType<typeof forecastService.getForecastDaily>>;
          if ('forecasts' in dailyValue && dailyValue.forecasts && dailyValue.forecasts.length > 0) {
            setDailyForecasts(dailyValue.forecasts);
          } else {
            setDailyForecasts([]);
          }
        } else {
          setDailyForecasts([]);
        }

        // Set hourly forecasts
        if (hourlyResponse.status === 'fulfilled') {
          const hourlyValue = hourlyResponse.value as Awaited<ReturnType<typeof forecastService.getForecastHourly>>;
          if ('forecasts' in hourlyValue && hourlyValue.forecasts && hourlyValue.forecasts.length > 0) {
            setHourlyForecasts(hourlyValue.forecasts);
          } else {
            setHourlyForecasts([]);
          }
        } else {
          setHourlyForecasts([]);
        }

        // Set rain forecasts
        if (rainResponse.status === 'fulfilled') {
          const rainValue = rainResponse.value as Awaited<ReturnType<typeof forecastService.getForecastRain>>;
          if ('forecasts' in rainValue && rainValue.forecasts && rainValue.forecasts.length > 0) {
            setRainForecasts(rainValue.forecasts);
          } else {
            setRainForecasts([]);
          }
        } else {
          setRainForecasts([]);
        }

        // Set minutely forecasts
        if (minutelyResponse.status === 'fulfilled') {
          const minutelyValue = minutelyResponse.value as Awaited<ReturnType<typeof forecastService.getForecastMinutely>>;
          if ('forecasts' in minutelyValue && minutelyValue.forecasts && minutelyValue.forecasts.length > 0) {
            setMinutelyForecasts(minutelyValue.forecasts);
          } else {
            setMinutelyForecasts([]);
          }
        } else {
          setMinutelyForecasts([]);
        }

        // Set infos
        if (infosResponse.status === 'fulfilled') {
          const infosValue = infosResponse.value as { success: boolean; data?: EventInfo[] };
          if (infosValue.success && infosValue.data && infosValue.data.length > 0) {
            setInfos(infosValue.data);
            setCurrentInfoIndex(0);
          } else {
            setInfos([]);
            setCurrentInfoIndex(0);
          }
        } else {
          setInfos([]);
          setCurrentInfoIndex(0);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setRadarActivity([]);
        setStationActivity([]);
        setCircuitData(null);
        setDailyForecasts([]);
        setHourlyForecasts([]);
        setInfos([]);
        setCurrentInfoIndex(0);
      }
    };

    fetchAvailability();
  // radarActivitySource is intentionally excluded: the main effect handles the initial
  // fetch; provider-driven refetches are handled by the dedicated effect below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, observationsEnabled, refreshCounter]);

  // Refetch radar activity when the map tile provider changes (L2 vs legacy).
  // Uses a ref to skip the initial mount (handled by fetchAvailability above).
  const prevRadarActivitySourceRef = useRef(radarActivitySource);
  useEffect(() => {
    if (prevRadarActivitySourceRef.current === radarActivitySource) return;
    prevRadarActivitySourceRef.current = radarActivitySource;

    if (!selectedEvent || !observationsEnabled) return;

    const refetchRadarActivity = async () => {
      try {
        const response = await activityService.getRadarActivity({
          season_year: selectedEvent.season_year,
          circuit_id: selectedEvent.circuit_id,
          start_date: selectedEvent.start_date,
          source: radarActivitySource,
        });
        if (response.activities && response.activities.length > 0) {
          setRadarActivity(response.activities);
        } else {
          setRadarActivity([]);
        }
        console.log(`[Nowcasting] Refetched radar activity for source=${radarActivitySource}`);
      } catch (err) {
        console.error('[Nowcasting] Error refetching radar activity:', err);
        setRadarActivity([]);
      }
    };

    refetchRadarActivity();
  }, [radarActivitySource, selectedEvent, observationsEnabled]);

  // Calculate radar activity availability ranges for timeline (only if observations enabled)
  // Memoize to prevent creating new object references on every render
  const radarActivityAvailability = useMemo(() => {
    if (!observationsEnabled || radarActivity.length === 0) return undefined;
    return computeActivityRanges(radarActivity);
  }, [observationsEnabled, radarActivity]);

  // Calculate station activity availability ranges for timeline (only if observations enabled)
  // Memoize to prevent creating new object references on every render
  const stationActivityAvailability = useMemo(() => {
    if (!observationsEnabled || stationActivity.length === 0) return undefined;
    return computeActivityRanges(stationActivity);
  }, [observationsEnabled, stationActivity]);

  // Calculate forecast issuance times and coverage ranges
  const forecastAvailability = useMemo<ForecastAvailability>(() => {
    const availability: ForecastAvailability = {};

    // Extract daily forecast issuance times
    if (dailyForecasts.length > 0) {
      availability.daily = dailyForecasts.map(f => new Date(f.value_date));
    }

    // Extract hourly forecast issuance times
    if (hourlyForecasts.length > 0) {
      availability.hourlyIssuance = hourlyForecasts.map(f => new Date(f.value_date));
    }

    // Extract hourly forecast coverage ranges
    if (hourlyForecasts.length > 0) {
      const ranges: { start: Date; end: Date }[] = [];

      hourlyForecasts.forEach(forecast => {
        if (forecast.hourly_forecasts && forecast.hourly_forecasts.length > 0) {
          const forecastTimes: number[] = [];

          forecast.hourly_forecasts.forEach(hf => {
            const datetimeValue = typeof hf.forecast_datetime === 'object' && hf.forecast_datetime !== null && 'value' in hf.forecast_datetime
              ? (hf.forecast_datetime as { value: string }).value
              : hf.forecast_datetime;

            if (datetimeValue) {
              forecastTimes.push(new Date(datetimeValue).getTime());
            }
          });

          if (forecastTimes.length > 0) {
            const minTime = Math.min(...forecastTimes);
            const maxTime = Math.max(...forecastTimes);
            ranges.push({
              start: new Date(minTime),
              end: new Date(maxTime)
            });
          }
        }
      });

      availability.hourly = ranges;
    }

    return availability;
  }, [dailyForecasts, hourlyForecasts]);

  // Fetch tiles when date range is selected
  const handleDateRangeChange = useCallback((startDate: Date, endDate: Date) => {
    // Check if this is an automatic update in live loop mode (Timeline auto-updating)
    // In that case, don't pause playback - let it continue looping
    // Use refs to avoid recreating this callback when playback state changes
    const isAutoUpdateInLiveLoop = isEventActiveRef.current && autoLoopRef.current && isPlayingRef.current;

    if (!isAutoUpdateInLiveLoop) {
      // Pause playback when user manually selects a different timeframe
      setIsPlaying(false);
      setIsTioPlaying(false);
      hasManuallyPausedRef.current = true; // Mark as manually paused so auto-play doesn't restart
    }

    setSelectedDateRange({ start: startDate, end: endDate });

    // Update selected timestamp for weather parameter components in header
    // Use endDate as fallback (we show latest observation in range, so end is appropriate placeholder)
    setSelectedTimestamp(endDate);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set loading state immediately
    setLoading(true);
    setError(null);

    // Debounce the API call
    debounceTimerRef.current = setTimeout(async () => {
      if (!selectedEvent) return;

      try {
        // Date objects from Timeline are already timestamps
        // Convert them directly to UTC for API filtering
        const timezone = selectedEvent.circuit?.time_zone || 'UTC';
        const startUTC = dayjs(startDate).utc();
        const endUTC = dayjs(endDate).utc();

        // Format timestamps as YYYYMMDDHHmmss for backend filtering
        // Floor start to beginning of minute so tiles at the exact selected minute are included
        // (Timeline dates may have non-zero seconds, which would exclude the round-minute tile)
        const startTimestamp = startUTC.startOf('minute').format('YYYYMMDDHHmmss');
        const endTimestamp = endUTC.endOf('minute').format('YYYYMMDDHHmmss');

        // Determine source based on event type
        const source = getEventSource(selectedEvent);

        // Fetch strategy:
        // - Radar tiles: only when observations enabled AND mapTileProvider is 'radar'
        // - Station observations: always when observations enabled (supports "radar not deployed
        //   but stations are" - user can view t.io forecast while keeping stations panel visible)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let tilesData: PromiseSettledResult<any>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let stationsData: PromiseSettledResult<any>;

        // For legacy events, always use the signed legacy listing (L2 has no legacy data).
        // This avoids a race with the mapTileProvider auto-switch effect.
        const effectiveProvider = source === 'legacy' ? 'radar' : mapTileProvider;

        if (observationsEnabled) {
          if (source === 'legacy') {
            [tilesData, stationsData] = await Promise.allSettled([
              radarTilesService.getRadarTiles({
                season_year: selectedEvent.season_year,
                circuit_id: selectedEvent.circuit_id,
                start_date: selectedEvent.start_date,
                source: 'legacy',
                start_timestamp: startTimestamp,
                end_timestamp: endTimestamp,
              }),
              stationObservationsService.getStationObservations(
                selectedEvent.season_year,
                selectedEvent.circuit_id,
                selectedEvent.start_date,
                source,
                startDate.toISOString(),
                endDate.toISOString()
              ),
            ]);
          } else if (effectiveProvider === 'radar-l2' || effectiveProvider === 'radar') {
            [tilesData, stationsData] = await Promise.allSettled([
              radarTilesService.getRadarTilesL2({
                start_timestamp: startTimestamp,
                end_timestamp: endTimestamp,
              }),
              stationObservationsService.getStationObservations(
                selectedEvent.season_year,
                selectedEvent.circuit_id,
                selectedEvent.start_date,
                source,
                startDate.toISOString(),
                endDate.toISOString()
              ),
            ]);
          } else if (effectiveProvider === 'radar-l2-numeric') {
            [tilesData, stationsData] = await Promise.allSettled([
              radarTilesService.getRadarTilesL2Pngd({
                start_timestamp: startTimestamp,
                end_timestamp: endTimestamp,
              }),
              stationObservationsService.getStationObservations(
                selectedEvent.season_year,
                selectedEvent.circuit_id,
                selectedEvent.start_date,
                source,
                startDate.toISOString(),
                endDate.toISOString()
              ),
            ]);
          } else {
            // On tomorrow.io: skip radar tiles (preserve existing for switch-back), but fetch stations
            // so the stations panel stays populated when radar is not deployed but stations are
            tilesData = { status: 'fulfilled' as const, value: { tiles: [], source: null } };
            stationsData = await stationObservationsService.getStationObservations(
              selectedEvent.season_year,
              selectedEvent.circuit_id,
              selectedEvent.start_date,
              source,
              startDate.toISOString(),
              endDate.toISOString()
            ).then((res) => ({ status: 'fulfilled' as const, value: res }))
              .catch((err) => ({ status: 'rejected' as const, reason: err }));
          }
        } else {
          tilesData = { status: 'fulfilled' as const, value: { tiles: [], source: null } };
          stationsData = { status: 'fulfilled' as const, value: { stations: [] } };
          setTiles([]);
          setStations([]);
        }

        // Process radar tiles (only when on radar/radar-l2/radar-l2-numeric source - when on tomorrow.io, preserve existing tiles)
        if (mapTileProvider === 'radar' || mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') {
          if (tilesData.status === 'fulfilled') {
            // Store the source for logging
            setTilesSource(tilesData.value.source || 'prod');

            // Backend already filters tiles by timestamp range, so no client-side filtering needed
            // Convert tiles to include dayjs objects (in event timezone for display)
            // Also prepend API base URL to proxy URLs
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
            const allProcessedTiles: RadarTile[] = tilesData.value.tiles.map((tile: { timestamp: string; url: string }) => {
              // L2 nowcast tiles have timestamps like "20251227140000+05" - parse base timestamp
              const hasNowcastOffset = tile.timestamp.includes('+');
              const baseTimestamp = hasNowcastOffset ? tile.timestamp.split('+')[0] : tile.timestamp;
              const forecastOffset = hasNowcastOffset ? parseInt(tile.timestamp.split('+')[1], 10) : undefined;

              // All timestamps are in UTC, convert to local timezone for display
              // Parse as UTC first, then convert to circuit timezone
              const datetime = dayjs.utc(baseTimestamp, 'YYYYMMDDHHmmss').tz(timezone)
                .add(forecastOffset ?? 0, 'minute');

              // Prepend API base URL to proxy URLs (relative paths)
              let url = tile.url;
              if (url.startsWith('/api/')) {
                url = `${apiBaseUrl}${url}`;
              }

              return {
                ...tile,
                url,
                datetime,
                ...(hasNowcastOffset ? { isNowcast: true, forecastOffset } : {}),
              };
            });

            // For radar-l2 and radar-l2-numeric: separate realtime and nowcast tiles
            // Nowcast tiles are identified by '+' in their timestamp (e.g. "20251227140000+05")
            let processedTiles: RadarTile[];
            if (mapTileProvider === 'radar-l2' || mapTileProvider === 'radar-l2-numeric') {
              processedTiles = allProcessedTiles.filter(t => !t.isNowcast);

              // Extract nowcast tiles from the latest base timestamp for use in extendedTiles
              const nowcastTiles = allProcessedTiles.filter(t => t.isNowcast);
              if (nowcastTiles.length > 0 && processedTiles.length > 0) {
                const lastBaseTimestamp = processedTiles[processedTiles.length - 1].timestamp;
                const latestNowcastTiles = nowcastTiles.filter(t =>
                  t.timestamp.startsWith(lastBaseTimestamp + '+')
                );
                setRadarL2NowcastTiles(latestNowcastTiles);
              } else {
                setRadarL2NowcastTiles([]);
              }
            } else {
              processedTiles = allProcessedTiles;
              setRadarL2NowcastTiles([]);
            }

            setTiles(processedTiles);

            // Dock based on user preference on live refresh.
            // Applies in both auto-loop and non-loop modes so that the
            // playback position is preserved across tile refreshes
            // (unless the user explicitly chose 'first').
            const shouldDock = isEventActiveRef.current && processedTiles.length > 0;
            const dock = playbackDockRef.current;
            let newIndex = 0;
            if (shouldDock) {
              if (dock === 'last') {
                newIndex = processedTiles.length - 1;
              } else if (dock === 'now') {
                const nowMs = dayjs.utc().valueOf();
                let closestIdx = 0;
                let closestDiff = Infinity;
                processedTiles.forEach((tile, idx) => {
                  const diff = Math.abs(tile.datetime.utc().valueOf() - nowMs);
                  if (diff < closestDiff) {
                    closestDiff = diff;
                    closestIdx = idx;
                  }
                });
                newIndex = closestIdx;
              } else if (dock === 'current') {
                const targetMs = currentTileDatetimeMsRef.current;
                const rangeStartMs = startDate.getTime();
                const rangeEndMs = endDate.getTime();
                if (targetMs == null || targetMs < rangeStartMs || targetMs > rangeEndMs) {
                  newIndex = 0;
                } else {
                  let closestIdx = 0;
                  let closestDiff = Infinity;
                  processedTiles.forEach((tile, idx) => {
                    const diff = Math.abs(tile.datetime.valueOf() - targetMs);
                    if (diff < closestDiff) {
                      closestDiff = diff;
                      closestIdx = idx;
                    }
                  });
                  newIndex = closestIdx;
                }
              }
              // dock === 'first' keeps newIndex = 0
            }
            setCurrentIndex(newIndex);
            // Update ref immediately to prevent race condition in interval callback
            currentIndexRef.current = newIndex;
          } else {
            console.error('Error fetching radar tiles:', tilesData.reason);
            setTiles([]);
          }
        }

        // Process station observations
        // When on tomorrow.io with observations: preserve existing stations if fetch returns empty
        // (minutely live updates can return empty; don't hide the panel)
        if (stationsData.status === 'fulfilled') {
          const newStations = stationsData.value.stations;
          if (
            isTioProvider(mapTileProvider) &&
            observationsEnabled &&
            newStations.length === 0
          ) {
            // Preserve existing stations - don't overwrite with empty on minutely update
            setStations((prev) => (prev.length > 0 ? prev : []));
          } else {
            setStations(newStations);
            setSelectedStationIndex(0);
          }
        } else {
          console.error('Error fetching station observations:', stationsData.reason);
          if (isTioProvider(mapTileProvider) && observationsEnabled) {
            setStations((prev) => (prev.length > 0 ? prev : []));
          } else {
            setStations([]);
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
        setTiles([]);
        setStations([]);
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce
    // Note: isEventActive, autoLoop, isPlaying are accessed via refs to avoid recreating
    // this callback when playback state changes (which would cause Timeline to re-render)
  }, [selectedEvent, setSelectedTimestamp, observationsEnabled, mapTileProvider, hasManuallyPausedRef, currentIndexRef, currentTileDatetimeMsRef, setIsPlaying, setIsTioPlaying, setCurrentIndex, setTiles, setRadarL2NowcastTiles]);

  return {
    // State values
    radarActivity,
    stationActivity,
    circuitData,
    dailyForecasts,
    hourlyForecasts,
    rainForecasts,
    minutelyForecasts,
    infos,
    currentInfoIndex,
    stations,
    selectedStationIndex,
    selectedDateRange,
    loading,
    error,
    tilesSource,

    // Setters
    setCurrentInfoIndex,
    setSelectedStationIndex,
    setStations,
    setSelectedDateRange,
    setRadarActivity,
    setStationActivity,
    setDailyForecasts,
    setHourlyForecasts,
    setRainForecasts,
    setMinutelyForecasts,
    setInfos,
    setCircuitData,
    setLoading,
    setError,
    setTilesSource,

    // Computed values
    radarActivityAvailability,
    stationActivityAvailability,
    forecastAvailability,

    // Handlers
    handleDateRangeChange,
    refreshActivityData,

    // Refs
    debounceTimerRef,
  };
}
